import { useAtomValue } from "@effect-atom/atom-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  legalLinkClass,
  legalListClass,
  LegalPageLayout,
  LegalSection,
} from "~/components/clawinstall/LegalPageLayout.tsx";
import { clawInstallIdentityAtom } from "~/lib/atoms/brand-identity.ts";
import { type HeadCtxLike, identityFromHeadCtx, marketingHead } from "~/lib/clawinstall-marketing-head.ts";

/** Head meta tags for the Acceptable Use Policy page (brand name follows the request identity). */
export const acceptableUseHead = (ctx?: HeadCtxLike) => {
  const identity = identityFromHeadCtx(ctx);
  return marketingHead({
    path: "/acceptable-use",
    title: `Acceptable Use Policy — ${identity.displayName}`,
    description:
      `The rules governing content, conduct, outreach, and automation on the ${identity.displayName} platform and its hosted agents.`,
    identity,
  });
};

/**
 * ClawInstall Acceptable Use Policy page
 *
 * Renders the finalized Acceptable Use Policy (docs/legal/clawinstall-acceptable-use.md).
 *
 * @since 1.0.0
 */
export const Route = createFileRoute("/clawinstall/acceptable-use")({
  head: acceptableUseHead,
  component: ClawInstallAcceptableUsePage,
});

function ClawInstallAcceptableUsePage() {
  const identity = useAtomValue(clawInstallIdentityAtom);
  const name = identity.displayName;
  return (
    <LegalPageLayout
      title={`${name} Acceptable Use Policy`}
      lastUpdated="September 5, 2026"
      version="1.1"
      current="/clawinstall/acceptable-use"
      testId="acceptable-use-page"
    >
      <section className="space-y-4">
        <p>
          This Acceptable Use Policy (the “<strong>AUP</strong>” or “<strong>Policy</strong>”) governs your access to
          and use of the {name}{" "}
          platform, websites, APIs, command-line tools, hosted agents, and related services (collectively, the “<strong>
            Service
          </strong>”) operated by {identity.legalLine} (“<strong>{name}</strong>,” “
          <strong>we</strong>,” “<strong>us</strong>,” or “<strong>our</strong>”). This Policy is incorporated into, and
          forms a part of, the {name} <Link to="/clawinstall/terms" className={legalLinkClass}>Terms of Service</Link>
          {" "}
          (the “<strong>Terms</strong>”) by reference. Capitalized terms not defined here have the meanings given in the
          Terms. If there is a conflict between this Policy and the Terms, the Terms control except where this Policy is
          more restrictive, in which case the more restrictive provision applies.
        </p>
        <p>
          By accessing or using the Service — including by deploying, configuring, or operating any automated agent
          (“<strong>Agent</strong>”) — you (“<strong>Customer</strong>,” “<strong>you</strong>,” or “<strong>
            your
          </strong>”) agree to this Policy.{" "}
          <strong>
            You are responsible for all activity conducted through your account and by any Agent you deploy, configure,
            instruct, or authorize, including activity performed with any credentials, tokens, mailboxes, or third-party
            accounts you connect to the Service.
          </strong>{" "}
          An Agent acts on your behalf; its conduct is your conduct for purposes of this Policy.
        </p>
        <p>If you do not agree to this Policy, you must not access or use the Service.</p>
      </section>

      <LegalSection title="1. Scope and Application">
        <p>This Policy applies to:</p>
        <ul className={legalListClass}>
          <li>All Customers and any individual or entity accessing the Service through a Customer's account.</li>
          <li>
            All Agents, automations, workflows, and integrations operated through the Service, including those that send
            outreach, browse the web, log in to third-party platforms, or act with connected credentials.
          </li>
          <li>
            All content, data, messages, and instructions submitted to, generated by, or transmitted through the Service
            (“<strong>Customer Content</strong>”).
          </li>
        </ul>
        <p>
          Because Agents perform automated outreach and act with connected credentials,{" "}
          <strong>you are accountable for every message an Agent sends and every action an Agent takes</strong>, whether
          or not you reviewed it in advance. You must supervise your Agents accordingly and configure them to comply
          with this Policy and with all applicable laws and third-party platform rules.
        </p>
      </LegalSection>

      <LegalSection title="2. Prohibited Content">
        <p>
          You may not use the Service to store, generate, transmit, distribute, or facilitate any content that:
        </p>
        <ul className={legalListClass}>
          <li>Is unlawful, defamatory, libelous, fraudulent, deceptive, or misleading.</li>
          <li>
            Infringes or misappropriates any patent, trademark, trade secret, copyright, right of publicity, or other
            intellectual property or proprietary right.
          </li>
          <li>
            Is sexually explicit; constitutes or promotes child sexual abuse material (CSAM); or sexualizes minors in
            any way.
          </li>
          <li>
            Harasses, threatens, bullies, defames, or incites violence against any person or group; or promotes
            terrorism, violent extremism, or self-harm.
          </li>
          <li>
            Constitutes hate speech or discrimination on the basis of race, ethnicity, national origin, religion, sex,
            gender identity, sexual orientation, disability, age, or any other protected characteristic.
          </li>
          <li>
            Contains malware, ransomware, spyware, viruses, worms, or any other malicious or harmful code.
          </li>
          <li>
            Discloses another person's private, personal, or sensitive information (including doxxing) without lawful
            basis and authorization.
          </li>
          <li>Violates the privacy, data-protection, or publicity rights of any third party.</li>
        </ul>
      </LegalSection>

      <LegalSection title="3. Prohibited Conduct">
        <p>You may not, and may not configure or instruct an Agent to:</p>
        <ul className={legalListClass}>
          <li>
            Violate any applicable local, state, national, or international law or regulation, or any third-party right.
          </li>
          <li>
            Engage in phishing, social engineering, impersonation, pretexting, or any scheme to obtain credentials,
            payment information, or other sensitive data under false pretenses.
          </li>
          <li>
            Misrepresent your identity or affiliation, or{" "}
            <strong>misrepresent that a message, profile, or interaction is human when it is automated</strong>{" "}
            (see Section 5).
          </li>
          <li>
            Send unsolicited bulk or commercial messages in violation of Section 4 (Anti-Spam, Consent, and Opt-Out).
          </li>
          <li>
            Engage in fraud, deceptive marketing, pyramid or Ponzi schemes, or market-manipulation activity.
          </li>
          <li>
            Collect, harvest, or scrape data in violation of a platform's terms, technical controls, or applicable law
            (see Section 5).
          </li>
          <li>
            Interfere with, disrupt, degrade, or impose an unreasonable load on the Service, its infrastructure, or any
            connected third-party platform.
          </li>
          <li>
            Resell, sublicense, or provide the Service to third parties except as expressly permitted by the Terms.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="4. Anti-Spam, Consent, and Opt-Out (Email and Social Outreach)">
        <p>
          Agents perform automated outreach across channels — including{" "}
          <strong>email and social platforms such as LinkedIn</strong>. You are solely responsible for ensuring that all
          outreach you conduct or automate through the Service complies with every applicable anti-spam, consent, and
          electronic-marketing law, including without limitation:
        </p>
        <ul className={legalListClass}>
          <li>
            the U.S. <strong>CAN-SPAM Act</strong>;
          </li>
          <li>
            Canada's Anti-Spam Legislation (<strong>CASL</strong>);
          </li>
          <li>
            the EU <strong>General Data Protection Regulation (GDPR)</strong> and the{" "}
            <strong>ePrivacy Directive</strong> (and national implementations); and
          </li>
          <li>
            any other applicable marketing, telemarketing, consumer-protection, or data-protection laws in the
            jurisdictions where your recipients are located.
          </li>
        </ul>
        <p>
          These obligations apply to{" "}
          <strong>both email and social/direct-message outreach</strong>. At a minimum, for all outreach conducted or
          automated through the Service, you must:
        </p>
        <ul className={legalListClass}>
          <li>
            <strong>Have a lawful basis and appropriate consent</strong>{" "}
            for contacting each recipient, where consent is required by applicable law (e.g., GDPR/ePrivacy, CASL).
          </li>
          <li>
            <strong>Accurately identify yourself and the sender.</strong>{" "}
            Do not use false, deceptive, or misleading header information, sender names, subject lines, or profile
            details.
          </li>
          <li>
            <strong>Clearly identify commercial messages</strong> as advertising or solicitation where required by law.
          </li>
          <li>
            <strong>Provide a functioning, conspicuous opt-out / unsubscribe mechanism</strong>{" "}
            in every commercial message, and include a valid physical postal address where required (e.g., CAN-SPAM).
          </li>
          <li>
            <strong>Honor opt-out, unsubscribe, and do-not-contact requests promptly</strong>{" "}
            and within the timeframe required by law, across all channels. Once a recipient opts out or asks not to be
            contacted, you must suppress that recipient and must not contact them again (including via a different
            Agent, mailbox, or platform account) absent renewed lawful consent. You must configure your Agents to detect
            and respect such requests.
          </li>
          <li>
            <strong>Maintain and apply suppression lists</strong>{" "}
            so that opt-outs persist across campaigns, Agents, and connected accounts.
          </li>
          <li>
            <strong>Not send to harvested, purchased, or scraped contact lists</strong>{" "}
            obtained in violation of law or a platform's terms.
          </li>
        </ul>
        <p>
          You are responsible for maintaining records sufficient to demonstrate consent and opt-out compliance.
          {name}{" "}
          is not the sender of your outreach and does not review or approve message content; compliance is your
          responsibility.
        </p>
      </LegalSection>

      <LegalSection title="5. Automation and Third-Party Platforms">
        <blockquote className="border-l-4 border-outline-variant/60 pl-4 italic">
          Agents act on your behalf on third-party platforms (for example, LinkedIn, email providers, and other
          services) and may operate with credentials, tokens, or accounts you connect. The following terms govern that
          activity.{" "}
          <strong>You assume all risk arising from how your Agents interact with third-party platforms.</strong>
        </blockquote>
        <p>
          When you use the Service to automate activity on, or connect credentials for, any third-party platform, you
          agree that:
        </p>
        <ul className={legalListClass}>
          <li>
            <strong>
              You must comply with each platform's Terms of Service, acceptable-use rules, automation and API policies,
              robots/technical access controls, and published rate limits.
            </strong>{" "}
            Many platforms restrict or prohibit automated access, scraping, bulk messaging, or unattended login. It is
            your responsibility to know and follow those rules for every platform you connect or target.
          </li>
          <li>
            <strong>You assume all risk of platform enforcement.</strong>{" "}
            Automated outreach and access may result in warnings, throttling, content removal, feature restrictions, or
            {" "}
            <strong>suspension or permanent termination of your accounts on those platforms</strong>.{" "}
            <strong>
              You — not {name}{" "}
              — bear the full risk and any resulting loss of such platform actions, including the loss of any connected
              account, its data, or its reputation.
            </strong>{" "}
            {name} makes no warranty that automated use of any third-party platform is permitted by that platform, and
            {" "}
            {name} is not responsible for any platform's enforcement decisions.
          </li>
          <li>
            <strong>No ToS-violating scraping or data collection.</strong>{" "}
            You may not use the Service to scrape, crawl, harvest, or extract data from any platform in violation of
            that platform's terms, technical access controls (including rate limits and anti-bot measures), or
            applicable law.
          </li>
          <li>
            <strong>No misrepresentation of automated messages.</strong>{" "}
            You must not configure an Agent to impersonate a specific real person, to falsify its identity, or to
            misrepresent automated or AI-generated messages as human-authored where such misrepresentation is deceptive,
            prohibited by the platform, or unlawful. Where a platform or applicable law requires disclosure of automated
            or AI-generated interactions, you are responsible for providing it.
          </li>
          <li>
            <strong>Respect rate limits and platform health.</strong>{" "}
            You must configure Agents to operate within platform-published and reasonable rate limits and to avoid
            patterns of activity that degrade, overload, or evade the protections of any third-party platform.
          </li>
          <li>
            <strong>Credential responsibility.</strong>{" "}
            You represent that you are authorized to connect and use any credentials, tokens, mailboxes, or accounts you
            provide, and that doing so does not violate any agreement between you and the relevant platform or account
            holder. You are responsible for securing and rotating your credentials.
          </li>
        </ul>
        <p>
          {name} provides tooling; <strong>you direct how it is used.</strong>{" "}
          Compliance with third-party platform rules and the consequences of automated activity on those platforms are
          your responsibility.
        </p>
      </LegalSection>

      <LegalSection title="6. Security and System Integrity">
        <p>You may not, and may not instruct an Agent to:</p>
        <ul className={legalListClass}>
          <li>
            Probe, scan, or test the vulnerability of the Service or any connected system without{" "}
            {name}'s prior written authorization.
          </li>
          <li>
            Breach, circumvent, or defeat any authentication, authorization, rate-limiting, or security measure of the
            Service or any third-party platform.
          </li>
          <li>
            Access, tamper with, or use any account, system, data, or network without authorization, or attempt to gain
            such access.
          </li>
          <li>
            Introduce malware or any code intended to harm, surveil, or gain unauthorized access to systems or data.
          </li>
          <li>
            Interfere with, intercept, or expropriate any system, data, or traffic of the Service or other users.
          </li>
          <li>
            Use the Service to develop, train, or operate tooling whose primary purpose is to violate the security or
            integrity of any system.
          </li>
        </ul>
        <p>
          You must promptly report any security vulnerability or suspected compromise you discover to{" "}
          <a href={`mailto:${identity.emails.security}`} className={legalLinkClass}>{identity.emails.security}</a>{" "}
          and must not exploit it.
        </p>
      </LegalSection>

      <LegalSection title="7. Resource Limits and Fair Use">
        <p>
          The Service is subject to capacity, rate, concurrency, and usage limits, which may be published or applied at
          {name}'s discretion. You may not:
        </p>
        <ul className={legalListClass}>
          <li>
            Exceed, evade, or attempt to circumvent any quota, rate limit, or resource allocation applicable to your
            plan or account.
          </li>
          <li>
            Use the Service in a manner that imposes an unreasonable or disproportionately large load on{" "}
            {name}'s infrastructure or that degrades Service performance or availability for other users.
          </li>
          <li>
            Operate cryptocurrency mining, distributed-computing, or other resource-abusive workloads not contemplated
            by your plan.
          </li>
          <li>
            Use automated means to create accounts, multiply usage, or otherwise artificially inflate consumption to
            evade limits or pricing.
          </li>
        </ul>
        <p>
          {name}{" "}
          may throttle, queue, or restrict activity that exceeds applicable limits or threatens Service stability.
        </p>
      </LegalSection>

      <LegalSection title="8. Enforcement and Suspension">
        <p>
          We may investigate any suspected violation of this Policy and cooperate with law enforcement as required.{" "}
          <strong>
            {name}{" "}
            reserves the right, but is not obligated, to monitor use of the Service for compliance with this Policy.
          </strong>
        </p>
        <p>
          If we determine, in our reasonable discretion, that you or an Agent under your control have violated this
          Policy, we may take any action we deem appropriate, including:
        </p>
        <ul className={legalListClass}>
          <li>Issuing a warning or requesting that you remediate the violation.</li>
          <li>Removing, disabling, or restricting access to offending Customer Content.</li>
          <li>
            Throttling, suspending, or terminating an Agent, a connected integration, your access to the Service, or
            your account.
          </li>
          <li>
            Suspending or terminating the Service in whole or in part, with or without notice depending on the severity
            of the violation and any legal or safety risk.
          </li>
          <li>Reporting illegal activity to the appropriate authorities.</li>
        </ul>
        <p>
          Where practicable and consistent with legal, safety, and security considerations, we will provide notice and
          an opportunity to cure. However, for violations that pose a risk of harm, legal liability, security
          compromise, or disruption to the Service or third parties — including ongoing spam, abuse, or platform-rule
          violations — we may act{" "}
          <strong>immediately and without prior notice</strong>. We are not liable to you for any action taken in
          good-faith enforcement of this Policy, and suspension or termination for a violation does not entitle you to
          any refund except as expressly stated in the Terms.
        </p>
      </LegalSection>

      <LegalSection title="9. Reporting Abuse">
        <p>
          If you become aware of any content, conduct, or Agent activity that violates this Policy, please report it to:
        </p>
        <p>
          <strong>Abuse reports:</strong>{" "}
          <a href={`mailto:${identity.emails.abuse}`} className={legalLinkClass}>{identity.emails.abuse}</a>
        </p>
        <p>
          When reporting, please include as much detail as possible: the nature of the abuse, any affected accounts or
          domains, relevant messages or URLs, timestamps, and your contact information. We review reports and take
          appropriate action but do not guarantee a specific outcome or response time for any individual report.
        </p>
        <p>
          For security vulnerabilities, contact{" "}
          <a href={`mailto:${identity.emails.security}`} className={legalLinkClass}>{identity.emails.security}</a>. For
          general support, contact{" "}
          <a href={`mailto:${identity.emails.support}`} className={legalLinkClass}>{identity.emails.support}</a>. For
          legal notices, contact{" "}
          <a href={`mailto:${identity.emails.legal}`} className={legalLinkClass}>{identity.emails.legal}</a>.
        </p>
      </LegalSection>

      <LegalSection title="10. Changes to This Policy">
        <p>
          We may update this Policy from time to time. When we make material changes, we will revise the “Last updated”
          date and version above and, where required, provide notice through the Service or by other reasonable means.
          Your continued use of the Service after a change takes effect constitutes acceptance of the revised Policy.
          The current version is always available at{" "}
          <a href={`https://${identity.canonicalHost}/acceptable-use`} className={legalLinkClass}>
            https://{identity.canonicalHost}/acceptable-use
          </a>.
        </p>
      </LegalSection>

      <LegalSection title="11. Relationship to the Terms of Service">
        <p>
          This Policy is <strong>incorporated by reference into the {name} Terms of Service</strong>{" "}
          and is binding on every Customer as part of the{" "}
          <Link to="/clawinstall/terms" className={legalLinkClass}>Terms</Link>. A breach of this Policy is a breach of
          the Terms. The Terms govern all matters not addressed here, including limitations of liability, disclaimers of
          warranty, indemnification, and the allocation of risk for automated activity and third-party platform
          enforcement described in Sections 4 and 5.
        </p>
      </LegalSection>
    </LegalPageLayout>
  );
}
